export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogConfig {
  level: LogLevel;
  enabledModules: Set<string>;
  enableTimestamps: boolean;
}

class Logger {
  private config: LogConfig = {
    level: 'warn', // Only show warnings and errors by default
    enabledModules: new Set(['CRITICAL', 'ERROR']),
    enableTimestamps: false,
  };

  private levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  enableModule(module: string): void {
    this.config.enabledModules.add(module);
  }

  disableModule(module: string): void {
    this.config.enabledModules.delete(module);
  }

  enableTimestamps(enabled: boolean): void {
    this.config.enableTimestamps = enabled;
  }

  private shouldLog(level: LogLevel, module?: string): boolean {
    // Always log if level is high enough
    if (this.levels[level] >= this.levels[this.config.level]) {
      return true;
    }

    // Or if module is specifically enabled
    if (module && this.config.enabledModules.has(module)) {
      return true;
    }

    return false;
  }

  private formatMessage(_level: LogLevel, module: string | undefined, message: string): string {
    const parts: string[] = [];
    
    if (this.config.enableTimestamps) {
      parts.push(new Date().toISOString());
    }
    
    if (module) {
      parts.push(`[${module}]`);
    }
    
    parts.push(message);
    
    return parts.join(' ');
  }

  debug(message: string, module?: string): void {
    if (this.shouldLog('debug', module)) {
      console.debug(this.formatMessage('debug', module, message));
    }
  }

  info(message: string, module?: string): void {
    if (this.shouldLog('info', module)) {
      console.info(this.formatMessage('info', module, message));
    }
  }

  warn(message: string, module?: string): void {
    if (this.shouldLog('warn', module)) {
      console.warn(this.formatMessage('warn', module, message));
    }
  }

  error(message: string, module?: string, error?: Error): void {
    if (this.shouldLog('error', module)) {
      const formattedMessage = this.formatMessage('error', module, message);
      if (error) {
        console.error(formattedMessage, error);
      } else {
        console.error(formattedMessage);
      }
    }
  }

  // Critical errors that should always be shown
  critical(message: string, error?: Error): void {
    const formattedMessage = this.formatMessage('error', 'CRITICAL', message);
    if (error) {
      console.error(formattedMessage, error);
    } else {
      console.error(formattedMessage);
    }
  }

  // Performance critical logs that can be toggled for debugging
  performance(message: string, module: string): void {
    if (this.shouldLog('debug', module) || this.config.enabledModules.has('PERFORMANCE')) {
      console.debug(this.formatMessage('debug', `PERF:${module}`, message));
    }
  }
}

export const logger = new Logger();

// Development helper to enable debug logging
if (typeof window !== 'undefined') {
  (window as any).enableDebugLogging = (modules?: string[]) => {
    logger.setLevel('debug');
    logger.enableTimestamps(true);
    if (modules) {
      modules.forEach(module => logger.enableModule(module));
    }
    console.info('Debug logging enabled. Use logger.enableModule("MODULE_NAME") to enable specific modules.');
  };
}